import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Field, controlClass } from './Field';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
  size?: 'sm' | 'md';
  leadingIcon?: ReactNode;
  /** A control on the right of the field: a clear button, a unit, a copy action. */
  trailing?: ReactNode;
  /** Stamps the value in the identifier face — use for chassis, VIN and invoice fields. */
  mono?: boolean;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    hideLabel,
    size = 'md',
    leadingIcon,
    trailing,
    mono = false,
    required,
    className,
    containerClassName,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      hideLabel={hideLabel}
      className={containerClassName}
    >
      {({ id, describedBy, invalid }) => (
        <div className="relative flex min-w-0 items-center">
          {leadingIcon ? (
            <span
              aria-hidden
              className={clsx(
                'pointer-events-none absolute text-faint [&>svg]:h-4 [&>svg]:w-4',
                size === 'sm' ? 'left-2.5' : 'left-3',
              )}
            >
              {leadingIcon}
            </span>
          ) : null}

          <input
            ref={ref}
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            required={required}
            disabled={disabled}
            className={clsx(
              controlClass({ size, invalid, disabled, hasLeadingIcon: Boolean(leadingIcon) }),
              mono && 'font-data tracking-[0.02em] tabular-nums',
              trailing && 'pr-9',
              className,
            )}
            {...rest}
          />

          {trailing ? (
            <span
              className={clsx(
                'absolute flex items-center',
                size === 'sm' ? 'right-1.5' : 'right-2',
              )}
            >
              {trailing}
            </span>
          ) : null}
        </div>
      )}
    </Field>
  );
});

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, hideLabel, required, className, containerClassName, rows = 3, ...rest },
  ref,
) {
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      hideLabel={hideLabel}
      className={containerClassName}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={clsx(
            'w-full min-w-0 rounded-control border bg-surface px-3 py-2.5 text-input text-ink placeholder:text-faint',
            'transition-[border-color]',
            'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-muted disabled:placeholder:text-faint',
            'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent',
            invalid ? 'border-danger' : 'border-line-strong hover:border-faint',
            className,
          )}
          {...rest}
        />
      )}
    </Field>
  );
});
